import { useMemo } from 'react'

import { useCatalogPanel } from 'features/catalog/hooks/use-catalog-panel'
import type { CatalogTableRowsResult } from 'features/catalog/hooks/use-catalog-table-rows'
import { useCatalogTableRows } from 'features/catalog/hooks/use-catalog-table-rows'
import type { FocusListOption } from 'features/catalog/types'
import { useEnhancedDialectFlag } from 'features/feedback/enhanced-dialect-flag'
import type { FilterOption } from 'features/filters/types'

/**
 * Every product hierarchy with records under `item`'s brand, in the page's
 * current date range/sidebar filters. Calls the exact hook the main catalog
 * table itself uses (`useCatalogTableRows`) with `selectedLens:
 * 'product_hierarchy'` and a copy of the live panel scoped down to this
 * brand — no new query, no new row-assembly code. Lives in `hooks/` (rather
 * than reading `useCatalogFiltersStore` inside `CatalogBrandProductsPanel`
 * itself) because store reads belong in a container or orchestrator hook,
 * not a view component.
 */
export function useBrandProductRows(params: {
  projectId: string
  item: FocusListOption
  defaultFilterList: FilterOption[]
}): CatalogTableRowsResult {
  const { projectId, item, defaultFilterList } = params

  const yogiqlFlag = useEnhancedDialectFlag()
  const panel = useCatalogPanel(yogiqlFlag.enabled, defaultFilterList)

  const brandPanel = useMemo(
    () => ({
      ...panel,
      filters: [
        ...panel.filters.filter((f) => f.field !== 'brand'),
        { field: 'brand', operator: 'is' as const, values: item.identifiers },
      ],
    }),
    [panel, item.identifiers],
  )

  return useCatalogTableRows({
    projectId,
    selectedLens: 'product_hierarchy',
    panel: brandPanel,
    defaultFilterList,
    groupCountries: false,
    // Irrelevant to this lens — assembleCatalogTableRows only branches on
    // groupThemes for selectedLens === 'theme'.
    groupThemes: true,
  })
}
